import HttpClientError from '@errors/HttpClientError';
import formatterCalculatorHtml from '@formatters/calculatorHtml';
import formatterDrugHtml from '@formatters/drugHtml';
import formatterExternalLinkHtml from '@formatters/externalLinkHtml';
import formatterThemeHtml from '@formatters/themeHtml';
import { type DrugInteraction } from '@models/DrugInteraction';
import { type TypeDrug } from '@models/TypeDrug';
import HttpClient from '@services/utils/HttpClient';

import { type IGetAllDrugsInputData } from './dtos/getAllDrugs/InputData';
import { type IGetAllDrugsOutputData } from './dtos/getAllDrugs/OutputData';
import { type IGetDetailsInputData } from './dtos/getDetails/InputData';
import { type IGetDetailsOutputData } from './dtos/getDetails/OutputData';
import { type IGetTypesDrugOutputData } from './dtos/getTypesDrug/OutputData';
import { type IVerifyInteractionsBetweenDrugsInputData } from './dtos/verifyInteractionsBetweenDrugs/InputData';
import { type IVerifyInteractionsBetweenDrugsOutputData } from './dtos/verifyInteractionsBetweenDrugs/OutputData';
import { type IDrugRepository } from './IDrugRepository';

interface IGetAllParams {
  id?: number;
}
interface IGetAllResponse {
  lista: {
    e_favorito: boolean;
    id: number;
    nome: string;
  }[];
}

interface IGetDetailsParams {
  id: number;
}
interface IGetDetailsResponse {
  conteudo: string;
  e_favorito: boolean;
  id: number;
  nome: string;
}

interface IGetTypesResponse {
  lista: {
    id: number;
    nome: string;
  }[];
}

interface IVerifyInteractionsBody {
  ids: number[];
}
interface IVerifyInteractionsResponse {
  lista: {
    bulas?: {
      link: string;
      medicamento: string;
    }[];
    conteudo: string;
    medicamento_interacao: string;
    medicamento_origem: string;
    nivel: string;
  }[];
}

class DrugService implements IDrugRepository {
  public async getAll(
    inputData: IGetAllDrugsInputData,
  ): Promise<IGetAllDrugsOutputData> {
    try {
      const params: IGetAllParams = {};
      if (inputData.typeDrugId) {
        params.id = inputData.typeDrugId;
      }
      const response = await HttpClient.get<IGetAllResponse, IGetAllParams>({
        path: 'medicamentos.php',
        params,
      });
      const output: IGetAllDrugsOutputData = {
        list: response.data.lista.map((item) => ({
          id: item.id,
          isFavorite: item.e_favorito,
          name: item.nome,
        })),
      };
      return output;
    } catch (err) {
      if (err instanceof HttpClientError) {
        throw new HttpClientError({
          body: err.body,
          message: err.message,
          response: err.response,
        });
      }
      if (err instanceof Error) {
        throw new Error(err.message);
      }
      throw new Error(String(JSON.stringify(err)));
    }
  }

  public async getDetails(
    inputData: IGetDetailsInputData,
  ): Promise<IGetDetailsOutputData> {
    try {
      const response = await HttpClient.get<
        IGetDetailsResponse,
        IGetDetailsParams
      >({
        path: 'medicamento.php',
        params: {
          id: inputData.id,
        },
      });
      let content = response.data.conteudo;
      content = content.replaceAll('\\', '');
      content = content.replace(
        /padding-inline-start:(\s+)?48px(;)?/g,
        'padding-inline-start: 24px;',
      );
      const maxWidth = 768;
      content = content.replaceAll(
        '<img',
        `<img style="width: ${window.innerWidth > maxWidth ? maxWidth : window.innerWidth - 16}px;"`,
      );

      const regexGetTagA = /<a\b[^>]*>(.*?)<\/a>/;
      const regexLinkGPMED =
        /<a[^>]*href\s*=\s*['"](.*?(?:medicamento\.php|topico\.php|med\.php|calculadora_\w+\.php).*?)['"][^>]*>(.*?)<\/a>(<\/a>)?/;
      let hasLink = true;
      while (hasLink) {
        const resultGetTagA = regexGetTagA.exec(content);
        hasLink = !!resultGetTagA;
        if (!resultGetTagA) {
          break;
        }
        let resultRegexLinkGPMED = regexLinkGPMED.exec(content);
        if (resultRegexLinkGPMED) {
          const textMatch = resultRegexLinkGPMED[0];

          let html = textMatch;
          let hasMoreThanOneLink = false;
          const regexHasMoreThanOneLink = /<a[^>]*>.*?<a[^>]*>.*?<\/a>.*?<\/a>/;
          hasMoreThanOneLink = regexHasMoreThanOneLink.test(html);
          while (hasMoreThanOneLink) {
            html = html.replace(/<a[^>]*>(<a[^>]*>.*?<\/a>)<\/a>/, '$1');
            hasMoreThanOneLink = regexHasMoreThanOneLink.test(html);
          }
          resultRegexLinkGPMED = regexLinkGPMED.exec(html) as RegExpExecArray;

          const href = resultRegexLinkGPMED[1];
          const name = resultRegexLinkGPMED[2];
          const drugToAppend = formatterDrugHtml(href, name);

          if (drugToAppend) {
            content = content.replaceAll(textMatch, drugToAppend);
          }

          const themeToAppend = formatterThemeHtml(href, name);
          if (themeToAppend) {
            content = content.replace(regexLinkGPMED, themeToAppend);
          }

          const calculatorToAppend = formatterCalculatorHtml(href, name);
          if (calculatorToAppend) {
            content = content.replaceAll(textMatch, calculatorToAppend);
          }
        } else {
          let resultRegexExternalLink = regexGetTagA.exec(
            content,
          ) as RegExpExecArray;

          const textMatch = resultRegexExternalLink[0];

          let html = textMatch;
          let hasMoreThanOneLink = false;
          const regexHasMoreThanOneLink = /<a[^>]*>.*?<a[^>]*>.*?<\/a>.*?<\/a>/;
          hasMoreThanOneLink = regexHasMoreThanOneLink.test(html);
          while (hasMoreThanOneLink) {
            html = html.replace(/<a[^>]*>(<a[^>]*>.*?<\/a>)<\/a>/, '$1');
            hasMoreThanOneLink = regexHasMoreThanOneLink.test(html);
          }
          resultRegexExternalLink =
            /<a\b[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/.exec(
              html,
            ) as RegExpExecArray;

          const href = resultRegexExternalLink[1];
          const name = resultRegexExternalLink[2];
          const externalLinkToAppend = formatterExternalLinkHtml(href, name);
          if (externalLinkToAppend) {
            content = content.replaceAll(textMatch, externalLinkToAppend);
          }
        }
      }
      const output: IGetDetailsOutputData = {
        content: content,
        id: response.data.id,
        isFavorite: response.data.e_favorito,
        name: response.data.nome,
      };
      return output;
    } catch (err) {
      if (err instanceof HttpClientError) {
        throw new HttpClientError({
          body: err.body,
          message: err.message,
          response: err.response,
        });
      }
      if (err instanceof Error) {
        throw new Error(err.message);
      }
      throw new Error(String(JSON.stringify(err)));
    }
  }

  public async getTypes(): Promise<IGetTypesDrugOutputData> {
    try {
      const response = await HttpClient.get<IGetTypesResponse>({
        path: 'tiposmedicamento.php',
      });
      const output: IGetTypesDrugOutputData = {
        list: response.data.lista.map((item) => {
          const typeDrug: TypeDrug = {
            id: item.id,
            name: item.nome,
          };
          return typeDrug;
        }),
      };
      return output;
    } catch (err) {
      if (err instanceof HttpClientError) {
        throw new HttpClientError({
          body: err.body,
          message: err.message,
          response: err.response,
        });
      }
      if (err instanceof Error) {
        throw new Error(err.message);
      }
      throw new Error(String(JSON.stringify(err)));
    }
  }

  public async verifyInteractions(
    inputData: IVerifyInteractionsBetweenDrugsInputData,
  ): Promise<IVerifyInteractionsBetweenDrugsOutputData> {
    try {
      const response = await HttpClient.post<
        IVerifyInteractionsResponse,
        IVerifyInteractionsBody
      >({
        path: 'interacoes.php',
        body: {
          ids: inputData.ids,
        },
      });
      const output: IVerifyInteractionsBetweenDrugsOutputData = {
        list: response.data.lista.map((item) => {
          const drugInteraction: DrugInteraction = {
            content: item.conteudo,
            interaction: item.medicamento_interacao,
            level: item.nivel,
            origin: item.medicamento_origem,
            labels:
              item.bulas?.map((bula, index) => ({
                drug: bula.medicamento,
                link: bula.link,
                uniqueId: index,
              })) || [],
          };
          return drugInteraction;
        }),
      };
      return output;
    } catch (err) {
      if (err instanceof HttpClientError) {
        throw new HttpClientError({
          body: err.body,
          message: err.message,
          response: err.response,
        });
      }
      if (err instanceof Error) {
        throw new Error(err.message);
      }
      throw new Error(String(JSON.stringify(err)));
    }
  }
}

export default new DrugService();
